// src/hooks/useTaskDetail.ts
// TaskDetail 組件的核心狀態管理 Hook - 實現 Local-first 樂觀更新

import { useState, useEffect, useCallback, useRef } from 'react';
import { Task, TaskStatus, TaskCategory, TaskCloseReason, TaskPriority, TaskEnergyLevel, TaskUpdateData } from '../types/task';
import { LocalObjective } from '../services/indexedDB';
import { TimeBox } from '../types/timeBox';
import { taskService } from '../features/task';
import { getCurrentUserId } from '../services/auth';
import { eventBus, EVENTS } from '../utils/eventBus';
import { logger } from '../utils/logger';
import { formatDateToString } from '../utils/dateUtils';
import {
  getTaskById as getTaskFromCache,
  updateTaskInCache,
  revertTaskInCache,
  deleteTaskFromCache
} from '../services/indexedDB';
import { isLocalNewer } from '../services/indexedDB/utils/cache-manager';
import { getTimeBoxCategory } from '../config/taskStyles';

const DEBOUNCE_DELAY = 500; // 500ms debounce

// ============================================
// 類型定義
// ============================================

export interface SplitTaskData {
  isRequested: boolean;
  subtasks: Array<{ title: string; notes?: string }> | null;
  isLoading: boolean;
  error: string | null;
}

export interface UseTaskDetailOptions {
  taskId: string | null;
  isOpen: boolean;
  initialTask?: Task;
  objectives?: LocalObjective[];
  onStatusChange?: (taskId: string, newStatus: TaskStatus, category?: TaskCategory, closeReason?: TaskCloseReason) => void;
  onSplitComplete?: (taskId: string) => void;
  onDropped?: (droppedTask: Task) => void;
  onClose?: () => void;
  showSuccess: (title: string, message: string) => void;
  showError: (title: string, message: string) => void;
  // create mode 相關
  mode?: 'create' | 'edit';
  initialTitle?: string;
}

export interface UseTaskDetailReturn {
  // 狀態
  task: Task | null;
  isLoading: boolean;
  error: string | null;
  isSubmitting: boolean;
  splitTaskData: SplitTaskData;
  objectiveTitle: string | null;

  // 任務操作
  toggleStatus: () => Promise<void>;
  dropTask: () => Promise<void>;
  deleteTask: () => Promise<void>;
  updatePlan: (plan: string | null) => Promise<void>;
  updateTargetDate: (targetDate: string) => Promise<void>;

  // 元數據更新
  updateTitle: (title: string) => Promise<void>;
  updateNotes: (notes: string | null) => Promise<void>;
  updateDuration: (minutes: number | null) => Promise<void>;
  updateCategory: (category: TaskCategory | null) => Promise<void>;
  updateObjective: (objectiveId: string | null) => Promise<void>;
  updatePriority: (priority: TaskPriority | null) => void | Promise<void>;
  updateEnergy: (energy: TaskEnergyLevel | null) => void | Promise<void>;
  updateLocation: (location: string | null) => Promise<void>;

  // 分割操作
  requestSplit: () => Promise<void>;
  handleSplitComplete: () => void;
  resetSplitData: () => void;

  // TimeBox 操作
  createInitialTimeBox: () => TimeBox | null;
}

// ============================================
// 初始狀態
// ============================================

const initialSplitData: SplitTaskData = {
  isRequested: false,
  subtasks: null,
  isLoading: false,
  error: null
};

// ============================================
// Hook 實現
// ============================================

export function useTaskDetail(options: UseTaskDetailOptions): UseTaskDetailReturn {
  const { taskId, isOpen, initialTask, objectives, onStatusChange, onSplitComplete, onDropped, onClose, showSuccess, showError, mode = 'edit', initialTitle } = options;

  // 核心狀態
  const [task, setTask] = useState<Task | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [splitTaskData, setSplitTaskData] = useState<SplitTaskData>(initialSplitData);
  const [objectiveTitle, setObjectiveTitle] = useState<string | null>(null);

  // Refs 用於樂觀更新回滾
  const originalTaskRef = useRef<Task | null>(null);
  // task.id 的 ref：供 debounce flush / unmount cleanup 使用，避免 stale closure
  const taskIdRef = useRef<string | null>(null);
  taskIdRef.current = task?.id ?? null;
  // initialTask 的 ref：避免物件參考每次不同導致 useEffect 無限觸發
  const initialTaskRef = useRef<Task | undefined>(initialTask);
  initialTaskRef.current = initialTask;
  // objectives 的 ref：避免 objectives 更新導致 fetchTask 重建進而觸發無限 fetch
  const objectivesRef = useRef(objectives);
  objectivesRef.current = objectives;

  // Debounced update 相關 refs
  const pendingUpdatesRef = useRef<TaskUpdateData>({});
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  // create mode lazy create refs
  // pendingTitleRef：暫存 initialTitle，避免進 state 觸發不必要 re-render
  const pendingTitleRef = useRef<string>(initialTitle?.trim() ?? '');
  // createPromiseRef：防重入 - 多個 handler 同時呼叫 ensureTaskCreated() 時，共享同一個 Promise
  const createPromiseRef = useRef<Promise<Task> | null>(null);

  // ============================================
  // 數據加載
  // ============================================

  const fetchTask = useCallback(async (id: string, preloaded?: Task) => {
    // 有 preloaded 時：立即顯示（快速），同時非同步讀 IndexedDB 補正
    // IndexedDB 包含 optimisticUpdate 寫入的最新值，比 Firestore listener snapshot 更可靠
    const applyObjectiveTitle = (t: Task) => {
      const matched = t.objectiveId
        ? objectivesRef.current?.find(o => o.id === t.objectiveId)
        : null;
      setObjectiveTitle(matched?.title ?? null);
    };

    if (preloaded) {
      setTask(preloaded);
      originalTaskRef.current = preloaded;
      setIsLoading(false);
      applyObjectiveTitle(preloaded);

      // 非同步讀 IndexedDB 補正：若 IndexedDB 有更新的資料（updatedAt 較新），覆蓋 preloaded
      let cancelled = false;
      getTaskFromCache(id).then(cached => {
        if (cancelled || !cached) return;
        if (isLocalNewer(cached, preloaded)) {
          setTask(cached);
          originalTaskRef.current = cached;
          applyObjectiveTitle(cached);
        }
      }).catch(err => {
        logger.error('IndexedDB 補正讀取失敗', err);
      });

      return () => { cancelled = true; };
    }

    setIsLoading(true);
    setError(null);

    try {
      const userId = getCurrentUserId();

      const cachedTask = await getTaskFromCache(id);
      if (cachedTask) {
        setTask(cachedTask);
        originalTaskRef.current = cachedTask;
        setIsLoading(false);
      }

      const fetchedTask = await taskService.getTaskById(id, userId);

      if (!fetchedTask) {
        throw new Error('Task not found');
      }

      setTask(fetchedTask);
      originalTaskRef.current = fetchedTask;
      await updateTaskInCache(id, fetchedTask);
      applyObjectiveTitle(fetchedTask);

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
      setError(errorMessage);
      logger.error('任務獲取失敗', err);
    } finally {
      setIsLoading(false);
    }
  // objectivesRef 透過 ref 存取，不需要列為依賴
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // create mode lazy create：首次互動時才建立 task
  // 回傳已建立的 Task，讓呼叫方可以直接使用（不依賴 setState 非同步性）
  const ensureTaskCreated = useCallback(async (): Promise<Task> => {
    // 已建立：直接回傳
    if (task) return task;

    // 防重入：若已有進行中的建立 Promise，等待同一個
    if (createPromiseRef.current) {
      return createPromiseRef.current;
    }

    // 發起建立
    const promise = (async () => {
      try {
        const userId = getCurrentUserId();
        const title = pendingTitleRef.current || 'New Task';
        const newTask = await taskService.createTask({ title, userId });
        setTask(newTask);
        originalTaskRef.current = newTask;
        eventBus.publish(EVENTS.TASK.CREATED, newTask.id, newTask);
        return newTask;
      } finally {
        // 無論成功或失敗都清除，確保下次重試能重新發起建立
        createPromiseRef.current = null;
      }
    })();

    createPromiseRef.current = promise;
    return promise;
  }, [task]);

  // create mode 生命週期管理：
  // - modal 關閉時重置；modal 開啟且 task 尚未建立時同步最新 initialTitle
  useEffect(() => {
    if (mode !== 'create') return;

    if (!isOpen) {
      createPromiseRef.current = null;
      pendingTitleRef.current = '';
      setTask(null);
      setObjectiveTitle(null);
    } else if (!task) {
      pendingTitleRef.current = initialTitle?.trim() ?? '';
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, mode, initialTitle, task]);

  // 監聽 taskId 變化（edit mode）
  // initialTaskRef 取代 initialTask 作為依賴，避免父層每次 render 傳入新物件參考導致無限 fetch
  useEffect(() => {
    if (mode === 'create') return; // create mode 由上面的 effect 處理
    if (isOpen && taskId) {
      fetchTask(taskId, initialTaskRef.current);
    } else {
      setTask(null);
      setError(null);
      setObjectiveTitle(null);
      originalTaskRef.current = null;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, taskId, fetchTask]);

  // ============================================
  // 樂觀更新輔助函數
  // ============================================

  const optimisticUpdate = useCallback(async (
    updates: TaskUpdateData,
    apiCall: () => Promise<void>,
    successMessage?: { title: string; message: string }
  ): Promise<void> => {
    if (!task) return;

    // 保存原始狀態用於回滾
    const originalTask = { ...task };
    originalTaskRef.current = originalTask;

    // 1. 樂觀更新本地狀態（null 語義為清空，轉為 undefined 後從 state 移除該 key）
    const localUpdates = Object.fromEntries(
      Object.entries(updates).map(([k, v]) => [k, v === null ? undefined : v])
    ) as Partial<Task>;
    const updatedTask = { ...task, ...localUpdates };
    setTask(updatedTask);

    // 2. 更新 IndexedDB
    try {
      await updateTaskInCache(task.id, updates);
    } catch (cacheError) {
      logger.error('IndexedDB更新失敗', cacheError);
      // 快取失敗不阻止流程
    }

    try {
      // 3. 同步到 Firestore
      await apiCall();

      // 4. 發布事件通知其他組件
      eventBus.publish(EVENTS.TASK.UPDATED, task.id, updatedTask);

      // 5. 顯示成功消息
      if (successMessage) {
        showSuccess(successMessage.title, successMessage.message);
      }

    } catch (error) {
      // 6. 回滾本地狀態
      setTask(originalTask);

      // 7. 回滾 IndexedDB
      try {
        await revertTaskInCache(task.id, originalTask);
      } catch (revertError) {
        logger.error('IndexedDB回滾失敗', revertError);
      }

      logger.error('任務更新失敗', error);
      showError('Update Failed', 'Failed to update task. Please try again.');
      throw error;
    }
  }, [task, showSuccess, showError]);

  // ============================================
  // Debounced 樂觀更新（用於快速連續操作的欄位）
  // ============================================

  // 立即 flush pending updates 到後端
  // taskIdRef 取代 task 依賴，避免 flushPendingUpdates 因 task state 變動而重建
  const flushPendingUpdates = useCallback(async () => {
    const currentTaskId = taskIdRef.current;
    if (!currentTaskId) return;

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    const updates = pendingUpdatesRef.current;
    if (Object.keys(updates).length === 0) return;

    pendingUpdatesRef.current = {};

    try {
      const userId = getCurrentUserId();
      await taskService.updateTask(currentTaskId, updates, userId);
      logger.dev('Debounced更新', '已同步到後端', updates);
    } catch (error) {
      logger.error('Debounced更新失敗', error);
      showError('Sync Failed', 'Some changes may not be saved. Please refresh.');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showError]);

  // Debounced 樂觀更新：立即更新本地，延遲發送到後端
  const debouncedOptimisticUpdate = useCallback((updates: TaskUpdateData): void => {
    if (!task) return;

    // 本地狀態只接受 Partial<Task>（不含 null），過濾 null 為 undefined
    const localUpdates = Object.fromEntries(
      Object.entries(updates).map(([k, v]) => [k, v === null ? undefined : v])
    ) as Partial<Task>;

    // 1. 立即更新本地狀態（樂觀更新）
    setTask(prevTask => prevTask ? { ...prevTask, ...localUpdates } : null);

    // 2. 累積 pending updates（保留 null，讓 flush 時傳給 Firestore）
    pendingUpdatesRef.current = { ...pendingUpdatesRef.current, ...updates };

    // 3. 更新 IndexedDB（同步本地快取）
    const currentTaskId = taskIdRef.current;
    if (currentTaskId) {
      updateTaskInCache(currentTaskId, updates).catch(err => {
        logger.error('IndexedDB更新失敗', err);
      });
    }

    // 4. 清除之前的定時器
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // 5. 設置新的定時器
    debounceTimerRef.current = setTimeout(() => {
      flushPendingUpdates();
    }, DEBOUNCE_DELAY);
  }, [task, flushPendingUpdates]);

  // Modal 關閉時 flush pending updates
  useEffect(() => {
    if (!isOpen) {
      flushPendingUpdates();
    }
  }, [isOpen, flushPendingUpdates]);

  // 組件卸載時 flush pending updates（全用 ref，不捕捉任何 state）
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      const updates = pendingUpdatesRef.current;
      const currentTaskId = taskIdRef.current;
      if (Object.keys(updates).length > 0 && currentTaskId) {
        const userId = getCurrentUserId();
        taskService.updateTask(currentTaskId, updates, userId).catch(err => {
          logger.error('卸載時同步失敗', err);
        });
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============================================
  // 任務狀態操作
  // ============================================

  const toggleStatus = useCallback(async () => {
    if (!task || !onStatusChange) return;

    const newStatus: TaskStatus = task.status === 'OPEN' ? 'CLOSED' : 'OPEN';

    // 直接更新本地狀態並通知父組件
    setTask({ ...task, status: newStatus });
    onStatusChange(task.id, newStatus, task.category, newStatus === 'CLOSED' ? 'completed' : undefined);
  }, [task, onStatusChange]);

  const dropTask = useCallback(async () => {
    if (!task) return;

    const userId = getCurrentUserId();
    const updates = {
      status: 'CLOSED' as TaskStatus,
      closeReason: 'dropped' as const,
      closedAt: new Date()
    };

    // 先通知父組件並關閉 Modal，Firestore 寫入在背景執行
    if (onDropped) {
      const droppedTask = { ...task, ...updates };
      onDropped(droppedTask);
    }
    if (onClose) {
      onClose();
    }

    await optimisticUpdate(
      updates,
      async () => {
        await taskService.updateTask(task.id, updates, userId);
      },
      { title: 'Task Dropped', message: 'Task has been marked as dropped.' }
    );
  }, [task, optimisticUpdate, onDropped, onClose]);

  const deleteTask = useCallback(async () => {
    if (!task) return;

    setIsSubmitting(true);

    try {
      const userId = getCurrentUserId();

      // 1. 從 Firestore 刪除
      await taskService.deleteTask(task.id, userId);

      // 2. 從 IndexedDB 刪除
      try {
        await deleteTaskFromCache(task.id);
      } catch (cacheError) {
        logger.error('IndexedDB刪除失敗', cacheError);
      }

      // 3. 發布事件
      eventBus.publish(EVENTS.TASK.DELETED, task.id);

      showSuccess('Task Deleted', 'Task has been permanently deleted.');

      if (onClose) {
        onClose();
      }
    } catch (error) {
      logger.error('刪除任務失敗', error);
      showError('Delete Failed', 'Failed to delete task. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [task, showSuccess, showError, onClose]);

  // ============================================
  // 元數據更新
  // ============================================

  const updatePlan = useCallback(async (newPlan: string | null) => {
    const t = task ?? (mode === 'create' ? await ensureTaskCreated() : null);
    if (!t) return;

    const userId = getCurrentUserId();
    const planValue = newPlan === '' ? null : newPlan;

    await optimisticUpdate(
      { plan: planValue },
      async () => {
        await taskService.updateTask(t.id, { plan: planValue }, userId);
      }
    );
  }, [task, mode, optimisticUpdate, ensureTaskCreated]);

  const updateTargetDate = useCallback(async (newTargetDate: string) => {
    const t = task ?? (mode === 'create' ? await ensureTaskCreated() : null);
    if (!t) return;

    const userId = getCurrentUserId();
    const targetDateValue = newTargetDate === '' ? null : newTargetDate;

    await optimisticUpdate(
      { targetDate: targetDateValue },
      async () => {
        await taskService.updateTask(t.id, { targetDate: targetDateValue }, userId);
      }
    );
  }, [task, mode, optimisticUpdate, ensureTaskCreated]);

  const updateTitle = useCallback(async (newTitle: string) => {
    const trimmedTitle = newTitle.trim();

    // 標題不可為空
    if (!trimmedTitle) {
      throw new Error('Title cannot be empty');
    }

    // create mode 且 task 尚未建立：ensureTaskCreated 會以 pendingTitleRef 建立 task
    // 無論 title 是否與 initialTitle 相同，都要建立 task（blur = 使用者確認）
    if (!task) {
      if (mode === 'create') {
        pendingTitleRef.current = trimmedTitle;
        await ensureTaskCreated();
        // 建立完成後 task state 已更新，title 已在 createTask 時帶入
      }
      return;
    }

    // 若標題沒有變更，不需要更新
    if (trimmedTitle === task.title) {
      return;
    }

    const userId = getCurrentUserId();

    await optimisticUpdate(
      { title: trimmedTitle },
      async () => {
        await taskService.updateTask(task.id, { title: trimmedTitle }, userId);
      }
    );
  }, [task, mode, optimisticUpdate, ensureTaskCreated]);

  const updateNotes = useCallback(async (newNotes: string | null) => {
    const t = task ?? (mode === 'create' ? await ensureTaskCreated() : null);
    if (!t) return;

    const notesValue = newNotes?.trim() || null;

    // 若備註沒有變更，不需要更新
    if ((notesValue ?? undefined) === t.notes) {
      return;
    }

    const userId = getCurrentUserId();

    await optimisticUpdate(
      { notes: notesValue },
      async () => {
        await taskService.updateTask(t.id, { notes: notesValue }, userId);
      }
    );
  }, [task, mode, optimisticUpdate, ensureTaskCreated]);

  const updateDuration = useCallback(async (minutes: number | null) => {
    const t = task ?? (mode === 'create' ? await ensureTaskCreated() : null);
    if (!t) return;

    const userId = getCurrentUserId();

    await optimisticUpdate(
      { estimatedTime: minutes },
      async () => {
        await taskService.updateTask(t.id, { estimatedTime: minutes }, userId);
      }
    );
  }, [task, mode, optimisticUpdate, ensureTaskCreated]);

  const updateCategory = useCallback(async (category: TaskCategory | null) => {
    const t = task ?? (mode === 'create' ? await ensureTaskCreated() : null);
    if (!t) return;

    const userId = getCurrentUserId();

    await optimisticUpdate(
      { category },
      async () => {
        await taskService.updateTask(t.id, { category }, userId);
      }
    );
  }, [task, mode, optimisticUpdate, ensureTaskCreated]);

  const updateObjective = useCallback(async (objectiveId: string | null) => {
    const t = task ?? (mode === 'create' ? await ensureTaskCreated() : null);
    if (!t) return;

    const userId = getCurrentUserId();

    // 樂觀更新 objectiveTitle：選中時從 objectives 查找，清除時為 null
    const newObjectiveTitle = objectiveId
      ? (objectives?.find(o => o.id === objectiveId)?.title ?? null)
      : null;

    const previousObjectiveTitle = objectiveTitle;
    setObjectiveTitle(newObjectiveTitle);

    try {
      await optimisticUpdate(
        { objectiveId },
        async () => {
          await taskService.updateTask(t.id, { objectiveId }, userId);
        }
      );
    } catch {
      setObjectiveTitle(previousObjectiveTitle);
    }
  }, [task, mode, objectives, objectiveTitle, optimisticUpdate, ensureTaskCreated]);

  // Priority 和 Energy 使用 debounced update（支援快速連續點擊）
  // create mode 下若 task 尚未建立，先 ensureTaskCreated 再 debounce
  const updatePriority = useCallback(async (priority: TaskPriority | null) => {
    if (!task && mode === 'create') await ensureTaskCreated();
    debouncedOptimisticUpdate({ priority });
  }, [task, mode, ensureTaskCreated, debouncedOptimisticUpdate]);

  const updateEnergy = useCallback(async (energy: TaskEnergyLevel | null) => {
    if (!task && mode === 'create') await ensureTaskCreated();
    debouncedOptimisticUpdate({ energyLevel: energy });
  }, [task, mode, ensureTaskCreated, debouncedOptimisticUpdate]);

  const updateLocation = useCallback(async (location: string | null) => {
    const t = task ?? (mode === 'create' ? await ensureTaskCreated() : null);
    if (!t) return;

    const userId = getCurrentUserId();
    const locationValue = location === '' ? null : location;

    await optimisticUpdate(
      { location: locationValue },
      async () => {
        await taskService.updateTask(t.id, { location: locationValue }, userId);
      }
    );
  }, [task, mode, optimisticUpdate, ensureTaskCreated]);

  // ============================================
  // 分割操作
  // ============================================

  const requestSplit = useCallback(async () => {
    if (!task) return;

    // 設置載入狀態
    setSplitTaskData({
      isRequested: true,
      subtasks: [],
      isLoading: true,
      error: null
    });

    try {
      const userId = getCurrentUserId();
      const currentDate = formatDateToString(new Date());

      logger.dev('AI任務分割', '開始請求');
      const result = await taskService.splitTaskWithAI(task, userId, { currentDate });
      logger.dev('AI任務分割', '完成', { subtaskCount: result.subtasks?.length });

      if (result.subtasks && Array.isArray(result.subtasks)) {
        setSplitTaskData({
          isRequested: true,
          subtasks: result.subtasks,
          isLoading: false,
          error: null
        });
      } else {
        throw new Error('Invalid subtasks format');
      }
    } catch (error) {
      logger.error('AI子任務生成失敗', error);

      setSplitTaskData({
        isRequested: true,
        subtasks: [],
        isLoading: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    }
  }, [task]);

  const handleSplitComplete = useCallback(() => {
    setSplitTaskData(initialSplitData);

    if (onSplitComplete && task) {
      onSplitComplete(task.id);
    }

    if (onClose) {
      onClose();
    }
  }, [task, onSplitComplete, onClose]);

  const resetSplitData = useCallback(() => {
    setSplitTaskData(initialSplitData);
  }, []);

  // ============================================
  // TimeBox 操作
  // ============================================

  const createInitialTimeBox = useCallback((): TimeBox | null => {
    if (!task) return null;

    const now = new Date();
    const startTime = new Date(now);
    startTime.setHours(startTime.getHours() + 1, 0, 0, 0);

    const endTime = new Date(startTime);
    const duration = task.estimatedTime || 60;
    endTime.setMinutes(endTime.getMinutes() + duration);

    const timeBoxCategory = getTimeBoxCategory(task.category);

    return {
      id: `temp-${Date.now()}`,
      itemType: 'timebox',
      userId: getCurrentUserId(),
      title: task.title,
      description: task.notes || '',
      startTime,
      endTime,
      category: timeBoxCategory,
      status: 'normal',
      location: task.location || '',
      taskId: task.id,
      createdAt: new Date()
    };
  }, [task]);

  // ============================================
  // 返回
  // ============================================

  return {
    // 狀態
    task,
    isLoading,
    error,
    isSubmitting,
    splitTaskData,
    objectiveTitle,

    // 任務操作
    toggleStatus,
    dropTask,
    deleteTask,
    updatePlan,
    updateTargetDate,

    // 元數據更新
    updateTitle,
    updateNotes,
    updateDuration,
    updateCategory,
    updateObjective,
    updatePriority,
    updateEnergy,
    updateLocation,

    // 分割操作
    requestSplit,
    handleSplitComplete,
    resetSplitData,

    // TimeBox 操作
    createInitialTimeBox,
  };
}
